import type { ColumnConfig, DefaultRowConfig } from "../SpreadsheetEditor/column-types";

// Re-export generic constants/types from the shared SpreadsheetEditor module
// so existing callers (and tests) can keep importing from
// `./quote-table-constants`.
export {
	BLANK_ROWS,
	COLUMN_TYPE_DESCRIPTIONS,
	COLUMN_TYPE_OPTIONS,
	COLUMN_WARNING_THRESHOLD,
	MAX_COLUMNS,
	SAMPLE_CELL_LABELS,
} from "../SpreadsheetEditor/column-types";
export type { ColumnTypeOption } from "../SpreadsheetEditor/column-types";

// ---------------------------------------------------------------------------
// QuoteTable-specific preset definitions
// ---------------------------------------------------------------------------

export interface PresetDefinition {
	columns: Omit<ColumnConfig, "localId">[];
	description?: string;
	name?: string;
	rows: DefaultRowConfig[];
}

export const DEFAULT_COLUMNS: Omit<ColumnConfig, "localId">[] = [
	{ columnType: "description", label: "Description of Scope", sortOrder: 1 },
	{ columnType: "unit_of_measurement", label: "Unit of Measurement", sortOrder: 2 },
	{ columnType: "response_quantity", label: "Quantity", sortOrder: 3 },
	{ columnType: "response_value", label: "Rate", sortOrder: 4 },
	{ columnType: "total", label: "Cost", sortOrder: 5 },
];

export const PRESETS: Record<string, PresetDefinition> = {
	preliminaries: {
		columns: DEFAULT_COLUMNS,
		name: "Preliminaries",
		description:
			"Project preliminaries — design, documentation, and meeting attendance items.",
		rows: [
			{ name: "E.g. Concept Design", unit_id: 7, sort_order: 1 },
			{ name: "E.g. Design Development", unit_id: 7, sort_order: 2 },
			{ name: "E.g. Contract Documentation", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Meeting Attendance", unit_id: 7, sort_order: 4 },
		],
	},
	trade_package: {
		columns: DEFAULT_COLUMNS,
		name: "Trade Package",
		description:
			"Materials, labour, plant and equipment items for a single trade package.",
		rows: [
			{ name: "E.g. Concrete Supply", unit_id: 4, sort_order: 1 },
			{ name: "E.g. Concrete Install", unit_id: 4, sort_order: 2 },
			{ name: "E.g. Contract Repairs", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Supervisor", unit_id: 8, sort_order: 4 },
		],
	},
	contingency: {
		columns: DEFAULT_COLUMNS,
		name: "Contingency",
		description: "Allowance items for unforeseen costs across the project.",
		rows: [
			{ name: "E.g. Contingency 1", unit_id: 3, sort_order: 1 },
			{ name: "E.g. Contingency 2", unit_id: 3, sort_order: 2 },
		],
	},
	professional_fees: {
		columns: DEFAULT_COLUMNS,
		name: "Professional Fees",
		description:
			"Consultant and professional fees — design, documentation, and project meetings.",
		rows: [
			{ name: "E.g. Concept Design", unit_id: 7, sort_order: 1 },
			{ name: "E.g. Design Development", unit_id: 7, sort_order: 2 },
			{ name: "E.g. Contract Documentation", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Meeting Attendance", unit_id: 7, sort_order: 4 },
		],
	},
	provisional_sums: {
		columns: DEFAULT_COLUMNS,
		name: "Provisional Sums",
		description:
			"Lump-sum allowances for items not yet fully scoped — supply and install.",
		rows: [
			{ name: "E.g. Landscaping (Supply & Install)", unit_id: 3, sort_order: 1 },
			{ name: "E.g. Irrigation (Supply & Install)", unit_id: 3, sort_order: 2 },
		],
	},
	other: {
		columns: DEFAULT_COLUMNS,
		name: "Other",
		description: "Custom table — fill in your own scope and items.",
		rows: [
			{ name: "", unit_id: null, sort_order: 1 },
			{ name: "", unit_id: null, sort_order: 2 },
			{ name: "", unit_id: null, sort_order: 3 },
			{ name: "", unit_id: null, sort_order: 4 },
		],
	},
};

export const PRESET_OPTIONS: { label: string; value: string; }[] = [
	{ value: "", label: "Custom" },
	{ value: "preliminaries", label: "Preliminaries" },
	{ value: "trade_package", label: "Trade: Materials, Labor, Plant & Equip." },
	{ value: "contingency", label: "Contingency" },
	{ value: "professional_fees", label: "Professional Fees" },
	{ value: "provisional_sums", label: "Provisional SUM/S" },
	{ value: "other", label: "Other" },
];
